/**
 * Server di posta: da dove partono gli avvisi.
 *
 * Il filo che tiene insieme la sezione: **finché la prova d'invio non riesce,
 * non parte niente.** Non è una precauzione decorativa — un server può
 * accettare l'autenticazione e poi rifiutare il mittente, e lo si scoprirebbe
 * il giorno in cui serviva davvero un avviso. Per questo ogni salvataggio
 * riporta la configurazione a «da verificare», e il riquadro in cima dice
 * sempre a che punto è.
 */
import { useState } from 'react';

import { ErroreApi } from '../../api/client.ts';
import { impostazioni as apiImpostazioni, type DatiSmtp } from '../../api/scadenziario.ts';
import type { ConfigurazioneSmtp, SicurezzaSmtp } from '../../api/tipi-dati.ts';
import { istante } from '../../componenti/formato.ts';
import { useAutenticazione } from '../../auth/contesto.tsx';
import { useConfigurazioneSmtp } from './configurazione.ts';

const SICUREZZE: { valore: SicurezzaSmtp; etichetta: string; aiuto: string }[] = [
  { valore: 'STARTTLS', etichetta: 'STARTTLS', aiuto: 'La scelta abituale, di solito sulla porta 587.' },
  { valore: 'TLS', etichetta: 'TLS', aiuto: 'Cifrata dal primo byte, di solito sulla porta 465.' },
  { valore: 'NESSUNA', etichetta: 'Nessuna', aiuto: 'In chiaro. Solo per il server di prova in sviluppo.' },
];

export function ServerDiPosta() {
  const { utente } = useAutenticazione();
  const { config, errore, rileggi } = useConfigurazioneSmtp();

  if (utente === null) return null;

  return (
    <>
      {errore !== null && <div className="avviso-errore" role="alert">{errore}</div>}

      {config === null ? (
        <div className="pannello"><p className="stato-caricamento">Caricamento…</p></div>
      ) : (
        <>
          <StatoInvio config={config} />
          {/* `utente.email` arriva dal contesto di autenticazione, che si
              rilegge da solo: la prova d'invio mostra quindi l'indirizzo di
              adesso, non quello che c'era all'apertura della pagina. */}
          <FormSmtp config={config} emailAmministratore={utente.email} onCambiato={rileggi} />
        </>
      )}
    </>
  );
}

/** Il riquadro in cima: si può inviare, sì o no, e perché. */
function StatoInvio({ config }: { config: ConfigurazioneSmtp }) {
  if (!config.configurato) {
    return (
      <div className="avviso-attenzione" role="status">
        <strong>Il server di posta non è ancora configurato.</strong> Finché non lo è, lo
        scadenziario non manda nessun avviso: le scadenze si vedono solo entrando
        nell'applicazione.
      </div>
    );
  }

  if (!config.attivo) {
    return (
      <div className="avviso-attenzione" role="status">
        <strong>Configurazione salvata, ma non ancora verificata.</strong> L'invio resta
        spento finché un messaggio di prova non arriva davvero a destinazione: un server
        può accettare l'accesso e poi rifiutare il mittente, e non è il caso di scoprirlo
        il giorno in cui serviva un avviso.
      </div>
    );
  }

  return (
    <div className="avviso-esito" role="status">
      <strong>Invio attivo.</strong> Ultima prova riuscita il {istante(config.verificataIl ?? '')}
      {config.modificataDa !== null && ` · configurata da ${config.modificataDa}`}
    </div>
  );
}

function FormSmtp({
  config, emailAmministratore, onCambiato,
}: {
  config: ConfigurazioneSmtp;
  emailAmministratore: string;
  onCambiato: () => void;
}) {
  const [host, setHost] = useState(config.host ?? '');
  const [porta, setPorta] = useState(config.porta ?? 587);
  const [sicurezza, setSicurezza] = useState<SicurezzaSmtp>(config.sicurezza ?? 'STARTTLS');
  const [utenteSmtp, setUtenteSmtp] = useState(config.utente ?? '');
  const [password, setPassword] = useState('');
  const [mittenteEmail, setMittenteEmail] = useState(config.mittenteEmail ?? '');
  const [mittenteNome, setMittenteNome] = useState(config.mittenteNome ?? 'Scadenziario');

  const [inSalvataggio, setInSalvataggio] = useState(false);
  const [erroreForm, setErroreForm] = useState<string | null>(null);
  const [conferma, setConferma] = useState<string | null>(null);

  /*
   * `null` = «manda al mio indirizzo», qualunque sia adesso.
   *
   * Il campo si può correggere a mano, ma finché nessuno lo tocca deve seguire
   * l'utente collegato: inizializzarlo una volta sola con `useState(email)`
   * congelava l'indirizzo al primo montaggio, e chi cambiava la propria email
   * si ritrovava qui ancora quella vecchia.
   */
  const [destinatarioScelto, setDestinatarioScelto] = useState<string | null>(null);
  const destinatarioProva = destinatarioScelto ?? emailAmministratore;

  const [inProva, setInProva] = useState(false);
  const [esitoProva, setEsitoProva] = useState<{ ok: boolean; messaggio: string } | null>(null);

  async function salva() {
    setErroreForm(null);
    setConferma(null);
    setEsitoProva(null);
    setInSalvataggio(true);

    const dati: DatiSmtp = {
      host: host.trim(),
      porta,
      sicurezza,
      utente: utenteSmtp.trim(),
      mittenteEmail: mittenteEmail.trim(),
      mittenteNome: mittenteNome.trim(),
      // Campo vuoto = non toccare quella salvata. È il motivo per cui si può
      // correggere una porta sbagliata senza andare a ricercare la password
      // della casella aziendale.
      ...(password !== '' ? { password } : {}),
    };

    try {
      await apiImpostazioni.salvaSmtp(dati);
      setPassword('');
      setConferma('Configurazione salvata. Ora manda un messaggio di prova per attivare l\'invio.');
      onCambiato();
    } catch (e) {
      setErroreForm(e instanceof ErroreApi ? e.message : 'Errore imprevisto');
    } finally {
      setInSalvataggio(false);
    }
  }

  async function prova() {
    setEsitoProva(null);
    setErroreForm(null);
    setInProva(true);

    try {
      const esito = await apiImpostazioni.provaSmtp(destinatarioProva.trim());
      setEsitoProva({ ok: esito.ok, messaggio: esito.messaggio });
      if (esito.ok) onCambiato();
    } catch (e) {
      setErroreForm(e instanceof ErroreApi ? e.message : 'Errore imprevisto');
    } finally {
      setInProva(false);
    }
  }

  const aiutoSicurezza = SICUREZZE.find((s) => s.valore === sicurezza)?.aiuto ?? '';

  return (
    <div className="pannello pannello-imbottito">
      <h2 className="sezione-titolo">Server di posta</h2>
      <p className="aiuto" style={{ maxWidth: '70ch', marginBottom: 16 }}>
        La casella da cui partono gli avvisi. Questi dati li fornisce chi gestisce la
        posta aziendale; la password viene salvata cifrata e non è più rileggibile da qui.
      </p>

      {conferma !== null && <div className="avviso-esito" role="status">{conferma}</div>}
      {erroreForm !== null && <div className="avviso-errore" role="alert">{erroreForm}</div>}

      <form onSubmit={(e) => { e.preventDefault(); void salva(); }}>
        <div className="campi-affiancati">
          <div className="campo">
            <label htmlFor="s-host">Indirizzo del server <span className="obbligatorio">obbligatorio</span></label>
            <input
              id="s-host"
              type="text"
              value={host}
              onChange={(e) => setHost(e.target.value)}
              placeholder="es. smtp.azienda.it"
              required
            />
          </div>

          <div className="campo">
            <label htmlFor="s-porta">Porta</label>
            <input
              id="s-porta"
              type="number"
              min={1}
              max={65535}
              value={porta}
              onChange={(e) => setPorta(Number(e.target.value))}
              style={{ minWidth: '100px' }}
            />
          </div>
        </div>

        <div className="campo">
          <label htmlFor="s-sic">Sicurezza della connessione</label>
          <select
            id="s-sic"
            value={sicurezza}
            onChange={(e) => setSicurezza(e.target.value as SicurezzaSmtp)}
          >
            {SICUREZZE.map((s) => (
              <option key={s.valore} value={s.valore}>{s.etichetta}</option>
            ))}
          </select>
          <p className="aiuto">{aiutoSicurezza}</p>
        </div>

        <div className="campo">
          <label htmlFor="s-utente">Casella di posta <span className="obbligatorio">obbligatoria</span></label>
          <input
            id="s-utente"
            type="text"
            value={utenteSmtp}
            onChange={(e) => setUtenteSmtp(e.target.value)}
            placeholder="es. scadenziario@azienda.it"
            autoComplete="off"
            required
          />
        </div>

        <div className="campo">
          <label htmlFor="s-pwd">
            Password
            {config.passwordImpostata
              ? <span className="tenue"> già salvata</span>
              : <span className="obbligatorio">obbligatoria</span>}
          </label>
          <input
            id="s-pwd"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={config.passwordImpostata ? '••••••••  (lascia vuoto per non cambiarla)' : ''}
            autoComplete="new-password"
            {...(config.passwordImpostata ? {} : { required: true })}
          />
          <p className="aiuto">
            {config.passwordImpostata
              ? 'Una password è già salvata. Lascia il campo vuoto per tenerla: serve solo se devi sostituirla.'
              : 'Viene salvata cifrata. Non comparirà più in questa schermata.'}
          </p>
        </div>

        <div className="campi-affiancati">
          <div className="campo">
            <label htmlFor="s-mitt">Indirizzo del mittente <span className="obbligatorio">obbligatorio</span></label>
            <input
              id="s-mitt"
              type="email"
              value={mittenteEmail}
              onChange={(e) => setMittenteEmail(e.target.value)}
              placeholder="es. scadenziario@azienda.it"
              required
            />
          </div>

          <div className="campo">
            <label htmlFor="s-mitt-nome">Nome del mittente</label>
            <input
              id="s-mitt-nome"
              type="text"
              value={mittenteNome}
              onChange={(e) => setMittenteNome(e.target.value)}
              placeholder="es. Scadenziario Marchisio"
            />
          </div>
        </div>

        <p className="aiuto">
          È il nome che i colleghi vedranno come mittente. Alcuni server rifiutano un
          mittente diverso dalla casella con cui ci si autentica.
        </p>

        <div className="form-azioni">
          <button type="submit" className="bottone bottone-principale" disabled={inSalvataggio}>
            {inSalvataggio ? 'Salvataggio…' : 'Salva configurazione'}
          </button>
        </div>

        <p className="aiuto tracciabilita">
          Ogni salvataggio riporta l'invio a «da verificare»: va rifatta la prova qui sotto.
        </p>
      </form>

      <h2 className="sezione-titolo" style={{ marginTop: 28 }}>Prova d'invio</h2>
      <p className="aiuto" style={{ maxWidth: '70ch', marginBottom: 12 }}>
        Manda un messaggio vero all'indirizzo indicato. Se arriva, l'invio degli avvisi si
        attiva; se non arriva, qui sotto compare il motivo restituito dal server.
      </p>

      <div className="campo-affiancato">
        <div className="campo" style={{ marginBottom: 0, flex: 1 }}>
          <label htmlFor="s-prova" className="solo-lettori-schermo">Destinatario della prova</label>
          <input
            id="s-prova"
            type="email"
            value={destinatarioProva}
            onChange={(e) => setDestinatarioScelto(e.target.value)}
          />
        </div>
        <button
          type="button"
          className="bottone"
          onClick={() => void prova()}
          disabled={inProva || !config.configurato}
        >
          {inProva ? 'Invio…' : 'Invia messaggio di prova'}
        </button>
      </div>

      {!config.configurato && (
        <p className="aiuto">Salva prima la configurazione.</p>
      )}

      {esitoProva !== null && (
        <div className={esitoProva.ok ? 'avviso-esito' : 'avviso-errore'} role="status" style={{ marginTop: 12 }}>
          {esitoProva.messaggio}
        </div>
      )}
    </div>
  );
}
